import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { House, ArrowLeft } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    if (!token) return setErr("Missing reset token. Use the link from your email.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-900 text-zinc-100">
      <div className="w-full max-w-md animate-fade-up">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-gold mb-8" data-testid="back-to-login">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <House size={24} weight="duotone" className="text-gold" />
          <span className="font-display text-2xl">Reagent Network</span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Set new password</div>
        <h2 className="font-display text-4xl tracking-tight">Welcome back in.</h2>

        <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid="reset-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">New password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Confirm password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input"
              className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
            />
          </div>
          {err && <div className="text-sm text-red-400" data-testid="reset-error">{err}</div>}
          <Button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-button"
            className="w-full h-11 bg-gold text-ink-900 hover:bg-gold-light font-semibold"
          >
            {loading ? "Updating..." : "Update password →"}
          </Button>
        </form>
      </div>
    </div>
  );
}
